/**
 * Background sync queue manager.
 *
 * Queues failed audio uploads and trip report saves for retry
 * when connectivity is restored. Uses the Background Sync API
 * when available, with a polling fallback.
 */

import {
  saveAudioRecording,
  getAllAudioRecordings,
  deleteAudioRecording,
  saveTripReportDraft,
  getAllTripReportDrafts,
  deleteTripReportDraft,
} from "./offlineDb";

const SYNC_AUDIO_TAG = "copilot-audio-upload";
const SYNC_TRIP_REPORT_TAG = "copilot-trip-report-save";

// Polling interval for fallback sync (30 seconds)
const POLL_INTERVAL_MS = 30_000;

let pollTimer: number | null = null;

/**
 * Queue an audio upload for background sync.
 * Saves the blob to IndexedDB and registers a sync event.
 */
export async function queueAudioUpload(
  blob: Blob,
  mimeType: string,
  duration: number,
): Promise<string> {
  const id = await saveAudioRecording(blob, mimeType, duration);
  await requestBackgroundSync(SYNC_AUDIO_TAG);
  return id;
}

/**
 * Queue a trip report save for background sync.
 */
export async function queueTripReportSave(
  payload: unknown,
): Promise<string> {
  const id = await saveTripReportDraft(payload);
  await requestBackgroundSync(SYNC_TRIP_REPORT_TAG);
  return id;
}

/**
 * Get the count of pending items in both queues.
 */
export async function getPendingQueueCounts(): Promise<{
  audioUploads: number;
  tripReports: number;
}> {
  try {
    const [audio, trips] = await Promise.all([
      getAllAudioRecordings(),
      getAllTripReportDrafts(),
    ]);
    return {
      audioUploads: audio.length,
      tripReports: trips.length,
    };
  } catch {
    return { audioUploads: 0, tripReports: 0 };
  }
}

/**
 * Attempt to flush all queued items (called when coming back online).
 */
export async function flushQueues(
  uploadAudio: (blob: Blob, mimeType: string) => Promise<boolean>,
  saveTripReport: (payload: unknown) => Promise<boolean>,
): Promise<{ audioSynced: number; tripsSynced: number }> {
  let audioSynced = 0;
  let tripsSynced = 0;

  // Flush audio uploads
  try {
    const audioItems = await getAllAudioRecordings();
    for (const item of audioItems) {
      try {
        const success = await uploadAudio(item.blob, item.mimeType);
        if (success) {
          await deleteAudioRecording(item.id);
          audioSynced++;
        }
      } catch {
        // Leave in queue for next retry
      }
    }
  } catch {
    // IndexedDB unavailable
  }

  // Flush trip report drafts
  try {
    const tripItems = await getAllTripReportDrafts();
    for (const item of tripItems) {
      try {
        const success = await saveTripReport(item.payload);
        if (success) {
          await deleteTripReportDraft(item.id);
          tripsSynced++;
        }
      } catch {
        // Leave in queue for next retry
      }
    }
  } catch {
    // IndexedDB unavailable
  }

  return { audioSynced, tripsSynced };
}

/**
 * Start periodic polling as fallback for browsers without Background Sync.
 */
export function startPollSync(
  uploadAudio: (blob: Blob, mimeType: string) => Promise<boolean>,
  saveTripReport: (payload: unknown) => Promise<boolean>,
): void {
  if (pollTimer !== null) return;

  pollTimer = window.setInterval(async () => {
    if (!navigator.onLine) return;
    await flushQueues(uploadAudio, saveTripReport);
  }, POLL_INTERVAL_MS);
}

export function stopPollSync(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

// ─── Internal helpers ───────────────────────────────────────────────

async function requestBackgroundSync(tag: string): Promise<void> {
  if (!("serviceWorker" in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    // Background Sync API (not available in all browsers)
    if ("sync" in registration) {
      await (registration as ServiceWorkerRegistration & { sync: { register: (tag: string) => Promise<void> } }).sync.register(tag);
      return;
    }
  } catch {
    // Background sync not available — fall through to poll-based fallback
  }
}
